import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Link, NavLink } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { selectUser } from '../../store/slices/userSlice';
import { useSelector } from 'react-redux';
import navigationItems from './navigationItems';


const MobileMenu = ({ isOpen, onClose }) => {
  const user = useSelector(selectUser);
  const closeButtonRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previouslyFocusedElement = document.activeElement;
    document.body.style.overflow = 'hidden';
    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key !== 'Tab' || !panelRef.current) return;

      const focusableElements = panelRef.current.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length === 0) {
        event.preventDefault();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];
      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
        previouslyFocusedElement.focus();
      }
    };
  }, [isOpen, onClose]);

  const displayName = user?.fullName || 'User';
  const userRole = user?.role 
    ? user.role.charAt(0).toUpperCase() + user.role.slice(1) 
    : 'User';
  return (
    <motion.div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label="Admin navigation"
      initial={{ x: '-100%' }}
      animate={{ x: 0 }}
      exit={{ x: '-100%' }}
      transition={{ type: 'tween', duration: 0.3 }}
      className="fixed inset-y-0 left-0 z-50 flex h-screen max-h-[100dvh] w-72 max-w-[calc(100vw-2rem)] flex-col bg-white text-gray-800 shadow-2xl"
    >
      <div className="flex h-20 flex-shrink-0 items-center justify-between border-b border-amber-100 px-4">
        <Link
          to="/"
          onClick={onClose}
          aria-label="Covrly Admin dashboard"
          className="relative flex min-w-0 items-center space-x-2"
        >
          <img
            src="https://symforklift.nyc3.cdn.digitaloceanspaces.com/fitness-logo.png"
            alt=""
            className="h-10 w-10 flex-shrink-0"
          />
          <span className="truncate text-xl font-bold">Covrly Admin</span>
        </Link>
        <button
          ref={closeButtonRef}
          type="button"
          onClick={onClose}
          aria-label="Close navigation menu"
          className="ml-2 flex-shrink-0 rounded-lg p-2 text-gray-600 hover:bg-amber-50 hover:text-amber-700"
        >
          <FiX className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      <nav aria-label="Primary navigation" className="min-h-0 flex-1 overflow-y-auto px-3 py-4">
        {navigationItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/'}
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center px-4 py-3 text-sm font-medium rounded-xl mb-1 transition-all ${
                isActive
                  ? 'bg-amber-50 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-amber-700'
              }`
            }
          >
            <item.icon aria-hidden="true" className="mr-3 h-5 w-5 flex-shrink-0" />
            <span>{item.name}</span>
          </NavLink>
        ))}
      </nav>

      <div className="flex-shrink-0 border-t border-amber-100 bg-amber-50/40 p-4">
        <div className="flex min-w-0 items-center space-x-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800">
            <span className="text-sm font-medium">
              {displayName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || 'A'}
            </span>
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-800">{displayName}</p>
            <p className="truncate text-xs text-amber-700">{userRole}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MobileMenu;
